/**
 * `R4-UIE1` — la pantalla «Exportación» del Management Tool.
 *
 * `R4-F2` (`PRD_R4.md:1154-1301`). Cinco bloques, en el orden del boceto
 * (`docs/prd/R4_bocetos/pantallas/HibExportacion.dc.html`):
 *
 *   ① el encabezado, con «Descargar CSV» como ÚNICA acción;
 *   ② el panel de filtros — Lotes, Desde, Hasta, «Aplicar filtro» y «Limpiar»;
 *   ③ la leyenda de dos líneas y la tabla de 14 o 12 columnas con su paginado;
 *   ④ el diccionario «Qué significa cada columna»;
 *   ⑤ la nota del brazo, sólo donde la derivación está declarada.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⟦rastro · `ADR-0082` · `R4-UIE1`⟧ LO QUE ESTA PANTALLA DEJÓ DE SER
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Hasta R4 esta pantalla **disparaba una cola y listaba un historial**: un
 * selector de lotes con un botón «Generar», el aviso «Exportación en cola, puede
 * tardar unos segundos» (`H-AUD-007`) y una tabla de cuatro columnas —fecha de
 * generación, lotes, registros y un botón «Descargar» por fila— con sus tres
 * estados y su «No hay exportaciones».
 *
 * **Nada de eso sobrevive, y la ausencia es el criterio.** `PRD_R4.md:1166`,
 * textual: *«La pantalla no tiene lista de exportaciones generadas, ni botón
 * “Generar”, ni avisos de exportación en cola: la tabla es lo que se va a exportar
 * y el archivo se arma al descargarlo»*; `R4-F2-CA-05` lo afirma por la ausencia
 * de los tres textos. El historial y el disparo salen también del borde
 * (`borde-exportacion.ts`, rastro ①).
 *
 * ⚠️ **Lo que sí se conserva de la pantalla vieja**, porque seguía siendo cierto:
 * que el brazo se lee de la DIRECCIÓN y no de la sesión (`AR5-m`), que el sello
 * sale de la configuración y nunca de un literal (`F4-CA-15`), y que un lote `""`
 * —el alta manual— necesita `rotuloDeLote` para no dibujar una casilla muda
 * (medido con el árbol de accesibilidad: era el único control sin nombre de las 18
 * pantallas).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ①  LAS 14 O 12 COLUMNAS SE DECIDEN POR LA CAPACIDAD DECLARADA, Y UN «NO SÉ»
 *        NO SE RESUELVE: **FALLA**
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0318` / B16 / `ADR-0304`-la-guarda: la variante de la fila la decide la
 * **capacidad de derivación declarada** y **nunca el nombre del brazo**. Un
 * `if (brazo === "puro")` ata la pantalla al vocabulario de una instancia y lo
 * pone rojo `gates/literales-de-brazo.spec.ts`; esconder las columnas «si vienen
 * vacías» falla en el Híbrido el día que nadie pidió humano.
 *
 * ⛔ **Y no hay `?? false` ni `?? true`.** `derivacionHabilitadaDe`
 * (`configuracion.ts`) devuelve `boolean | null`, y su propio docblock declara que
 * **no puede lanzar** porque corre en un render — y que *«lo único que puede
 * [impedir un `?? false`] es que el caso “no declarado” tenga un examen propio en
 * la pantalla que lo consuma»*. Esta pantalla es ese consumidor, así que acá el
 * `null` **corta**:
 *
 *   · `?? false` **silenciaría las columnas afirmando que ese brazo no deriva**,
 *     en una tabla que alguien va a analizar;
 *   · `?? true` las emitiría sobre un brazo del que este proceso no sabe nada
 *     (`CP-R1-1-F1-053`: *«una cautela que se activa siempre no es una cautela: es
 *     el comportamiento»*).
 *
 * ⇒ **Es la MISMA decisión que el servidor ya tomó**:
 * `apps/admin-web/src/exportacion-de-participantes.ts` §`capacidadDe` deja que
 * `derivacionHabilitadaDe` **lance** y lo dice con estas palabras: *«es mejor que
 * el operador no reciba nada que un archivo que afirma algo que nadie pudo
 * verificar»*. Acá, en vez de lanzar —una excepción en el render deja la consola
 * en blanco—, la pantalla **dice qué falta y no pide nada**.
 *
 * ⛔⛔ **SE CONDICIONAN LAS CUATRO COSAS, NO UNA.** Lo midió el examen ciego de
 * esta etapa: *«un mutante que pinta las catorce SIEMPRE sobrevivía porque la
 * entrada “Pidió humano:” del diccionario sí estaba condicionada»*. Las cuatro
 * son ⓐ los dos `<th>`, ⓑ los dos `<td>` (incluida **la celda de fecha y hora**),
 * ⓒ la entrada del diccionario (CA-75) y ⓓ la nota del brazo (CA-74). Las cuatro
 * leen **la misma** `conDerivacion`: una sola decisión con cuatro consumidores, no
 * cuatro decisiones.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ②  LO ELEGIDO NO ES LO APLICADO, Y SON DOS ESTADOS SEPARADOS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R4.md:1190`: *«Lo que se elige o se escribe en los filtros no cambia nada
 * hasta tocar “Aplicar filtro”: ni la tabla ni el CSV»*. `CA-16` lo mide sobre el
 * paginado y **`CA-80` lo mide sobre el ARCHIVO**: con «Exp H2» aplicado y «Exp H»
 * marcado después sin aplicar, el CSV trae 2 filas.
 *
 * ⇒ `borrador` es lo que el operador tocó; `aplicado` es lo único que ven la
 * consulta de la tabla **y la URL de la descarga**. Un solo estado compartido
 * pasaría `CA-16` (porque el refetch depende del efecto) y rompería `CA-80` sin
 * que nada más se moviera.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ③  EL CÁLCULO ES DEL SERVIDOR. ACÁ NO SE CALCULA NI SE REORDENA NADA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * «Recibió», «Motivo», el «—», el orden de las filas, el orden de los lotes del
 * desplegable y el filtro por brazo son de `R4-AN1` / `R4-AWE1` / `participants`.
 * Esta pantalla **pinta las filas en el orden en que llegan** y pinta `motivo.texto`
 * tal cual. Reordenar o recalcular acá duplicaría la regla y el CSV —que arma el
 * servidor— dejaría de coincidir con la tabla (CA-89).
 *
 * ⚠️ **Y el nombre del archivo tampoco se arma acá** (regla 17): lo pone
 * `admin-web` en `Content-Disposition` y lo lee la primitiva de `R4-UI0`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ④  LOS DÍAS SON TEXTO `aaaa-mm-dd` DE PUNTA A PUNTA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `<input type="date">` entrega `aaaa-mm-dd` y así viaja al borde. **Acá no se
 * construye ni un `Date`**: `new Date("2026-09-21").toISOString()` es medianoche
 * **UTC**, así que al oeste de Greenwich el día que llegaría al servidor sería el
 * anterior — y ése es justamente el defecto que R4 viene a arreglar. El límite del
 * día lo decide el servidor en hora de Chile (`B13`, `fecha-local.ts`).
 *
 * La leyenda muestra los días `dd-mm-aaaa` (`PRD_R4.md:1205`) y para eso
 * **reordena los tres trozos del texto**, sin parsear fechas: ver `diaVisible`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ⟦rastro · `ADR-0082` · `R4-UIE1` vuelta 2 · `[5/10/2026]`⟧ ⭐⭐ ⑤ **LA CELDA DE
 *        FECHA Y HORA ES LA PRIMITIVA DE `R4-UI0`, ENSANCHADA — NO UNA COPIA**
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * En la primera vuelta esta pantalla importó un
 * ~~`CeldaDeFechaYHoraDelCable`~~ propio, en `src/exportacion/`. **El motivo era
 * verdadero y el `reviewer` lo verificó**: `celda-fecha-hora.tsx` aceptaba sólo un
 * `Date` y el cable de Exportación manda el texto **ya formateado por Postgres**
 * (`FORMATO_DE_LA_LISTA = "DD-MM-YYYY HH24:MI"`).
 *
 * ⛔ **Pero una celda propia es la SEGUNDA DEFINICIÓN que `ADR-0386` prohibe**
 * —una primitiva, un sitio—, y eso no lo decide un `dev`. El dueño lo decidió al
 * revisar: **se ensancha la primitiva a `Date | string`** —la realidad del cable—
 * y la copia se retira. El archivo propio **ya no existe**; lo que lo reemplaza es
 * `import { CeldaFechaHora } from "../celda-fecha-hora.tsx"`, con el rastro entero
 * del por qué en el encabezado de ESE archivo.
 *
 * ⚠️ Lo que la pantalla le pasa es el `string` del cable **tal cual**: no construye
 * ningún `Date` (eso reintroduciría la zona del navegador) y no formatea nada.
 * `""` —la ausencia declarada del contrato— la primitiva lo dibuja como nada.
 */

import { useEffect, useMemo, useState } from "react"
import type { JSX } from "react"
import { BotonDeDescarga } from "../boton-de-descarga.tsx"
import { CeldaFechaHora } from "../celda-fecha-hora.tsx"
import { useBordeExportacion } from "../contexto-borde-exportacion.tsx"
import { useConfiguracion } from "../contexto-configuracion.tsx"
import { derivacionHabilitadaDe } from "../configuracion.ts"
import { IdentificadorAcortado } from "../identificador-acortado.tsx"
import { Paginado } from "../paginado.tsx"
import { SelectorDeLotes } from "../selector-de-lotes.tsx"
import type { LoteDelSelector } from "../selector-de-lotes.tsx"
import { useBrazo } from "../brazo.ts"
import { rotuloDeLote } from "../padron/formato.ts"
import { esBrazo, losOtrosBrazos, selloDelBrazo } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"
import type {
  FilaDeExportacion,
  FiltroDeExportacion,
  TamanoDeExportacion,
} from "./borde-exportacion.ts"

/**
 * ⭐ `CA-68` · los SEIS tamaños de «Filas por página», **con 1.000**.
 *
 * ⛔ No se importa `FILAS_POR_PAGINA` de `paginado.tsx`: ésa es la escala de R3
 * —cinco tamaños— y `R4-UI0` dejó escrito por qué no se le agrega el sexto
 * («cambia de golpe Conversaciones, Participantes y Auditoría, que ningún CA de R4
 * pide cambiar, y enciende `R3-UI1.gate.ts:1225-1240` y `R3-UI6.gate.ts:161`»).
 * La escala llega a la primitiva por el prop `escala`, que es el opt-in que esa
 * etapa creó — y con él viene el punto de miles de `CA-68`/`PRD:1258` («1.184»).
 *
 * ⚠️ Está tipada contra el cable (`TamanoDeExportacion`), así que un séptimo
 * tamaño acá no compila: cualquier otro número es `400` del otro lado (RT 4).
 */
const ESCALA_DE_FILAS = [10, 50, 100, 200, 500, 1000] as const satisfies readonly TamanoDeExportacion[]

/**
 * ⭐ `PRD_R4.md:1258` · *«al abrir la pantalla, 10»*. Es el primer elemento de la
 * escala de R4 y no `FILAS_POR_PAGINA_AL_ABRIR` (que es el de la de R3): el valor
 * coincide hoy, y leerlo de la escala propia es lo que impide que un cambio en la
 * escala de R3 mueva el arranque de esta pantalla sin que nada lo nombre.
 */
const FILAS_AL_ABRIR: TamanoDeExportacion = ESCALA_DE_FILAS[0]

/**
 * ⭐ `CA-68` · la ayuda que va **al lado** del selector de filas
 * (`PRD_R4.md:1258`: *«A su lado, la ayuda “10, 50, 100, 200, 500 o 1.000”»*).
 *
 * ⛔ Es texto de la PANTALLA y no de la primitiva, y `R4-UI0` lo dejó dicho en su
 * `acceptance` («La ayuda … es texto de la pantalla (`R4-UIE1`)»): entra por
 * `children`, que es el hueco declarado de `Paginado` para los controles propios
 * de una pantalla, a la izquierda de «Anterior».
 */
const AYUDA_DE_FILAS = "10, 50, 100, 200, 500 o 1.000"

/**
 * El filtro vacío. **Es un valor de módulo y su identidad importa:** `Limpiar` y
 * el cambio de brazo lo vuelven a poner, y React descarta el re-render cuando el
 * estado es el MISMO objeto — así «limpiar lo que ya estaba limpio» no dispara una
 * consulta de más.
 */
const SIN_FILTRO: FiltroDeExportacion = { lotes: [], desde: null, hasta: null }

/**
 * `aaaa-mm-dd` → `dd-mm-aaaa`, **reordenando trozos y sin parsear una fecha**.
 *
 * ⛔ No hay `new Date` acá a propósito (docblock ④): esto es presentación de un
 * texto que ya es un día de Chile, y meter un `Date` en el medio reintroduce la
 * zona del navegador en una conversión que no la necesita.
 *
 * ⚠️ Un texto que no tenga las tres partes se devuelve tal cual: si el filtro
 * llegara con otra forma, se VE en la leyenda en vez de desaparecer.
 */
function diaVisible(dia: string): string {
  const [anio = "", mes = "", diaDelMes = ""] = dia.split("-")
  return diaDelMes === "" ? dia : `${diaDelMes}-${mes}-${anio}`
}

/** `""` ⇒ `null`: el filtro vacío es ausencia, no una cadena vacía en la URL. */
function diaONada(valor: string): string | null {
  return valor === "" ? null : valor
}

/**
 * ⭐ `CA-08`, `CA-14`, `CA-15` · el rótulo del desplegable CERRADO
 * (`PRD_R4.md:1181` punto 1).
 *
 * ⛔ Lo fija la pantalla y llega por parámetro a la primitiva: `R4-UI0` declaró
 * que fijarlo adentro la dejaría inservible para Participantes, cuyos CA de R3
 * fijan «N elegido(s).».
 *
 * ⚠️ Con UN lote elegido dice su NOMBRE, y pasa por `rotuloDeLote`: el lote del
 * alta manual es la cadena vacía en el cable, y sin el rótulo el campo quedaría en
 * blanco justo cuando hay exactamente un lote elegido.
 */
function rotuloDelSelector(elegidos: readonly string[]): string {
  if (elegidos.length === 0) return "Todos los lotes"
  const unico = elegidos[0]
  if (elegidos.length === 1 && unico !== undefined) return rotuloDeLote(unico)
  return `${elegidos.length} lotes elegidos`
}

/**
 * ⭐ `CA-28`–`CA-32` · la PRIMERA línea de la leyenda (`PRD_R4.md:1196-1203`).
 *
 * Se arma de tres piezas —el sello, el sujeto y la cola del rango— y termina
 * siempre en «Fecha y hora en hora de Chile.».
 *
 * ⛔ **El sello sale de `selloDelBrazo` y no de un literal** (`F4-CA-15`, gate de
 * `R4-G0`): copiar «KAI · Brazo Híbrido» del PRD pone rojo ese gate y rompe en
 * cualquier otra instancia. Sin brazo en la dirección no se inventa ninguno: la
 * frase dice lo que sabe.
 */
function primeraLineaDeLaLeyenda(brazo: Brazo | null, filtro: FiltroDeExportacion): string {
  const sujeto =
    filtro.lotes.length === 0
      ? "los participantes de todos los lotes del brazo"
      : "los participantes de los lotes elegidos"

  const cola =
    filtro.desde !== null && filtro.hasta !== null
      ? ` y lo que hizo cada uno entre el ${diaVisible(filtro.desde)} y el ${diaVisible(filtro.hasta)}.`
      : filtro.desde !== null
        ? ` y lo que hizo cada uno desde el ${diaVisible(filtro.desde)}.`
        : filtro.hasta !== null
          ? ` y lo que hizo cada uno hasta el ${diaVisible(filtro.hasta)}.`
          : " con sus estados actuales, como en Participantes."

  const encabezado =
    brazo === null ? "Lo que se va a exportar" : `Lo que se va a exportar de ${selloDelBrazo(brazo)}`

  return `${encabezado}: ${sujeto}${cola} Fecha y hora en hora de Chile.`
}

/**
 * ⭐ `CA-33`/`CA-34` · la SEGUNDA línea, textual del PRD (`:1205`). Son dos textos
 * y el eje es **si hay alguna fecha aplicada**, no si hay rango completo: con una
 * sola fecha se calcula como con rango (`PRD_R4.md:1252`).
 */
function segundaLineaDeLaLeyenda(filtro: FiltroDeExportacion): string {
  const conFechas = filtro.desde !== null || filtro.hasta !== null
  const cierre =
    "«Descargar CSV» baja todas las filas del filtro con estas mismas columnas."
  return conFechas
    ? `Al lado de cada estado va la fecha y hora de la primera vez que pasó dentro del rango. «—»: no recibió ningún mensaje inductor en el rango, y el motivo dice por qué. ${cierre}`
    : `Al lado de cada estado va su fecha y hora como la guarda Participantes: la primera vez y, en Opt-out, la de la baja vigente. ${cierre}`
}

/**
 * ⭐ `CA-35`/`CA-36` · **los nombres de las columnas, en el orden del PRD**
 * (`:1210-1226`), y los mismos en la pantalla y en el CSV.
 *
 * ⛔ Las dos de «Pidió humano» van en una constante aparte y se insertan en su
 * posición (11 y 12): escribir dos listas completas —una de catorce y otra de
 * doce— es la clase de par que diverge en la primera corrección, y el CSV del
 * servidor usa exactamente este criterio (`csv.ts` §`COLUMNA_PIDIO_HUMANO_FECHA`).
 */
const COLUMNAS_ANTES_DE_DERIVACION = [
  "ID",
  "Nombre",
  "Teléfono",
  "Lote",
  "Recibió mensaje inductor en el rango",
  "Motivo",
  "Sin interacción",
  "Sin interacción · fecha y hora",
  "Siguió con el chatbot",
  "Siguió con el chatbot · fecha y hora",
] as const

const COLUMNAS_DE_DERIVACION = ["Pidió humano", "Pidió humano · fecha y hora"] as const

const COLUMNAS_DESPUES_DE_DERIVACION = ["Opt-out", "Opt-out · fecha y hora"] as const

function columnasDe(conDerivacion: boolean): readonly string[] {
  return [
    ...COLUMNAS_ANTES_DE_DERIVACION,
    ...(conDerivacion ? COLUMNAS_DE_DERIVACION : []),
    ...COLUMNAS_DESPUES_DE_DERIVACION,
  ]
}

/**
 * ⭐ `CA-72`/`CA-73`/`CA-75` · **el diccionario, textual de la tabla del PRD**
 * (`:1271-1287`) y en ese orden.
 *
 * La entrada de «Pidió humano:» va marcada `soloConDerivacion` y se filtra con la
 * MISMA capacidad que las columnas (docblock ①): el Brazo Puro no la tiene
 * (`CA-75`).
 */
const DICCIONARIO: readonly {
  readonly entrada: string
  readonly texto: string
  readonly soloConDerivacion?: true
}[] = [
  {
    entrada: "ID, Nombre, Teléfono y Lote:",
    texto: "los datos del participante, como en Participantes.",
  },
  {
    entrada: "Recibió mensaje inductor en el rango:",
    texto:
      "«Sí» si a esa persona le llegó (Entregado o Leído, según Meta) al menos un mensaje inductor, el mensaje con el que empieza cada envío, dentro del rango; «No» si no.",
  },
  {
    entrada: "Motivo:",
    texto:
      "solo cuando dice «No»: ya estaba dado de baja, Meta no lo entregó (con el motivo), Meta todavía no confirmó la entrega o no se le envió ningún mensaje inductor en el rango. Sin fechas: Meta no lo entregó, todavía no confirmó la entrega o nunca se le envió ninguno.",
  },
  {
    entrada: "Sin interacción:",
    texto: "«Sí» si recibió y no escribió ni tocó ningún botón dentro del rango.",
  },
  {
    entrada: "Siguió con el chatbot:",
    texto:
      "«Sí» si escribió al menos un mensaje, o tocó un botón que marca que siguió con el chatbot, dentro del rango.",
  },
  {
    entrada: "Pidió humano:",
    texto: "«Sí» si tocó el botón para hablar con una persona dentro del rango.",
    soloConDerivacion: true,
  },
  {
    entrada: "Opt-out:",
    texto: "«Sí» si se dio de baja dentro del rango. Sin fechas: «Sí» si tiene la baja vigente.",
  },
  {
    entrada: "Fecha y hora de cada estado:",
    texto:
      "la primera vez que pasó dentro del rango, en hora de Chile. Sin fechas: la primera vez; en Opt-out, la fecha de la baja vigente.",
  },
  {
    entrada: "Sin fechas:",
    texto:
      "los cuatro estados son los de Participantes y sus fechas son las guardadas para cada participante (en Opt-out «No» va sin fecha), aunque la persona nunca haya recibido un mensaje inductor; «Recibió mensaje inductor» y «Motivo» se muestran igual, como información.",
  },
]

/** ⭐ `CA-74` · la nota del brazo, textual (`PRD_R4.md:1287`). */
const NOTA_DEL_BRAZO =
  "La columna Pidió humano aparece porque este brazo tiene derivación a humano."

/** Regla 21 (`PRD_R4.md:1404`), textual: los tres estados de la tabla y el de la descarga. */
const CARGANDO = "Cargando…"
const LECTURA_FALLIDA = "No se pudo hablar con el servidor."
const SIN_FILAS = "No hay participantes para mostrar."
const DESCARGA_FALLIDA = "No se pudo descargar el CSV. Intenta de nuevo."

export function PantallaExportacion(): JSX.Element {
  /**
   * ⭐ `AR5-m` · el brazo sale de la DIRECCIÓN y no de la sesión. Es lectura: esta
   * pantalla **no manda el brazo en ningún pedido** —va adelante de la ruta y el
   * servidor lo resuelve— y eso no cambia acá.
   */
  const brazoDeLaRuta = useBrazo()
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null

  const config = useConfiguracion()
  const borde = useBordeExportacion()

  /**
   * ⛔⛔ **LA CAPACIDAD, Y EL «NO SÉ» QUE NO SE RESUELVE** (docblock ①).
   *
   * `null` significa dos cosas y las dos impiden dibujar la tabla: que la
   * instancia no declaró `derivacionPorBrazo`, o que la declaró y este brazo no
   * está. Sin brazo en la dirección tampoco hay sobre qué preguntar.
   */
  const conDerivacion: boolean | null =
    brazo === null ? null : derivacionHabilitadaDe(config, brazo)

  // ── Los filtros: lo ELEGIDO y lo APLICADO, separados (docblock ②) ───────────
  const [lotesElegidos, setLotesElegidos] = useState<readonly string[]>([])
  const [desdeEscrito, setDesdeEscrito] = useState("")
  const [hastaEscrito, setHastaEscrito] = useState("")
  const [aplicado, setAplicado] = useState<FiltroDeExportacion>(SIN_FILTRO)

  // ── La tabla ────────────────────────────────────────────────────────────────
  const [lotesDelBrazo, setLotesDelBrazo] = useState<readonly string[]>([])
  const [filas, setFilas] = useState<readonly FilaDeExportacion[]>([])
  const [total, setTotal] = useState(0)
  const [pagina, setPagina] = useState(1)
  const [filasPorPagina, setFilasPorPagina] = useState<TamanoDeExportacion>(FILAS_AL_ABRIR)
  const [cargando, setCargando] = useState(true)
  const [errorDeLista, setErrorDeLista] = useState<string | null>(null)
  const [errorDeDescarga, setErrorDeDescarga] = useState<string | null>(null)

  /**
   * ⭐ `CA-26`/`CA-27` · `Desde` posterior a `Hasta` **no se aplica**.
   *
   * ⛔ La comparación es de TEXTO y es correcta justamente por la forma:
   * `aaaa-mm-dd` es lexicográficamente monótona, así que `"2026-09-27" >
   * "2026-09-21"` sin revivir ningún `Date` — exactamente como lo compara
   * `apps/admin-web/src/exportacion-de-participantes.ts` §`leerFiltro`. Dos
   * criterios de comparación para el mismo par de días serían dos respuestas.
   */
  const rangoInvertido = desdeEscrito !== "" && hastaEscrito !== "" && desdeEscrito > hastaEscrito

  /**
   * ⭐ `CA-25` · **cambiar de brazo en la dirección vacía los filtros**
   * (`PRD_R4.md:1190`), sin recargar.
   *
   * ⚠️ Vuelve a poner `SIN_FILTRO` —el MISMO objeto— así que en una pantalla que
   * ya estaba sin filtro esto no cambia el estado y no dispara una consulta de
   * más. `CA-24` (recargar) no necesita nada: no hay nada persistido.
   */
  useEffect(() => {
    setLotesElegidos([])
    setDesdeEscrito("")
    setHastaEscrito("")
    setAplicado(SIN_FILTRO)
    setPagina(1)
  }, [brazo])

  /**
   * Los lotes del desplegable se leen una vez por brazo.
   *
   * ⛔ **No se ordenan acá.** El orden —del más reciente al más antiguo y, a igual
   * fecha de creación, por nombre— lo da `participants`
   * (`modules/participants/src/lecturas.ts:440`) y lo mide `CA-11`. La primitiva
   * tampoco ordena, y eso está declarado en su docblock.
   *
   * ⚠️ Un fallo acá es del SELECTOR y no de la tabla: deja la lista vacía y no
   * contamina `errorDeLista`, que es el que dibuja «No se pudo hablar con el
   * servidor.» en la tabla.
   */
  useEffect(() => {
    if (brazo === null || conDerivacion === null) return
    let cancelado = false

    void borde
      .lotes()
      .then((lista) => {
        if (!cancelado) setLotesDelBrazo(lista)
      })
      .catch(() => {
        if (!cancelado) setLotesDelBrazo([])
      })

    return () => {
      cancelado = true
    }
  }, [borde, brazo, conDerivacion])

  /**
   * Cada cambio de filtro APLICADO, de página o de tamaño es una consulta nueva.
   *
   * ⛔ `CA-78` · un fallo **deja las filas que había**: `setFilas` no se toca en el
   * `catch`. `PRD_R4.md:1266`: *«si ya había filas a la vista, quedan como
   * estaban»*. Vaciarlas haría que un 500 pasajero se lea como «este filtro no
   * tiene a nadie».
   */
  useEffect(() => {
    if (brazo === null || conDerivacion === null) return
    let cancelado = false
    setCargando(true)
    setErrorDeLista(null)

    void borde
      .lista({ ...aplicado, page: pagina, pageSize: filasPorPagina })
      .then((resultado) => {
        if (cancelado) return
        setFilas(resultado.items)
        setTotal(resultado.total)
        setCargando(false)
      })
      .catch(() => {
        if (cancelado) return
        setErrorDeLista(LECTURA_FALLIDA)
        setCargando(false)
      })

    return () => {
      cancelado = true
    }
  }, [borde, brazo, conDerivacion, aplicado, pagina, filasPorPagina])

  /**
   * Los lotes para la primitiva. El cable manda NOMBRES desnudos
   * (`ExportLotesResponseBody = readonly string[]`), así que el `id` —lo que se
   * manda al borde— y el `nombre` —lo que se lee— son el mismo valor, salvo el
   * lote del alta manual: `id` sigue siendo `""` y el nombre es el rótulo.
   */
  const lotesParaElSelector = useMemo<readonly LoteDelSelector[]>(
    () => lotesDelBrazo.map((lote) => ({ id: lote, nombre: rotuloDeLote(lote) })),
    [lotesDelBrazo],
  )

  /** ⭐ `CA-17`/`CA-69` · aplicar vuelve a la página 1 (`PRD_R4.md:1186`). */
  const aplicar = (): void => {
    setAplicado({
      lotes: lotesElegidos,
      desde: diaONada(desdeEscrito),
      hasta: diaONada(hastaEscrito),
    })
    setPagina(1)
  }

  /** ⭐ `CA-22`/`CA-23` · «Limpiar» vacía lo elegido Y lo aplicado, y vuelve a la 1. */
  const limpiar = (): void => {
    setLotesElegidos([])
    setDesdeEscrito("")
    setHastaEscrito("")
    setAplicado(SIN_FILTRO)
    setPagina(1)
  }

  /**
   * ⭐ `CA-80` · **el CSV usa el filtro APLICADO y no lo elegido.** Es `aplicado`
   * y no `{ lotes: lotesElegidos, … }`, y es la única línea que separa los dos
   * criterios.
   */
  const descargar = async (): Promise<void> => {
    setErrorDeDescarga(null)
    await borde.descargar(aplicado)
  }

  /**
   * ⛔ **El tamaño que devuelve la primitiva se VALIDA contra la escala, no se
   * castea.** `Paginado` entrega un `number` (sale de `Number(evento.target.value)`)
   * y el cable sólo acepta los seis de `ESCALA_DE_FILAS`: un `as TamanoDeExportacion`
   * compilaría y le dejaría pedir un tamaño que el servidor contesta `400`. Un valor
   * fuera de la escala se IGNORA en vez de mandarse — la escala la pinta esta misma
   * pantalla, así que un valor de afuera es un defecto de la primitiva y no una
   * elección del operador.
   */
  const cambiarFilasPorPagina = (nuevo: number): void => {
    const permitido = ESCALA_DE_FILAS.find((tamano) => tamano === nuevo)
    if (permitido === undefined) return
    setFilasPorPagina(permitido)
  }

  const columnas = columnasDe(conDerivacion === true)

  /**
   * ⭐⭐ **EL AISLAMIENTO POR BRAZO, DICHO EN LA PANTALLA — SE CONSERVA, Y ES UN
   * CONFLICTO ENTRE DOS VOCES DEL PRODUCTO QUE SE RESUELVE A FAVOR DE LO QUE YA
   * ESTÁ.**
   *
   * `[18/9/2026]` Esta nota se agregó porque **el Anexo B la daba por RESUELTA y el
   * texto no existía de este lado**: su fila decía *«Exportación | “…desde acá no se
   * ven…” | “…desde aquí no se ven…”»*, y el 17/9 se corrigió EL BOCETO para que
   * dijera «aquí». Alguien la miró, la cerró como diferencia de TEXTO, y la nota no
   * estaba. Lo que faltaba no era la palabra: era la nota.
   *
   * ⛔⛔ **Y `R4-F2` no la nombra.** Su §«La pantalla, elemento por elemento»
   * (`PRD_R4.md:1156-1166`) ENUMERA siete elementos —marco, «Descargar CSV»,
   * filtros, Tabla 1, paginado, diccionario y nota del brazo— y esta nota no está en
   * la lista; el boceto de R4 (`HibExportacion.dc.html`) tampoco la dibuja.
   *
   * ⇒ **se conserva**, por el criterio del dueño —*lo que existe se mantiene, lo
   * nuevo se agrega, el conflicto se revisa*— y porque ningún CA de R4 la prohíbe:
   * `CA-05` nombra TRES textos que no pueden aparecer («Generar», «Exportaciones
   * generadas», «No hay exportaciones») y éste no es ninguno. La enumeración del PRD
   * describe lo que la pantalla TIENE que tener, no una lista cerrada de lo que
   * puede tener. Borrarla por omisión reabriría el hueco que el 18/9 costó
   * encontrar, y lo reabriría **sin que ningún CA de R4 lo mida**.
   *
   * ⚠️⚠️ **LO QUE SÍ ES UNA DIVERGENCIA DE TEXTO Y VA AL INFORME, NO SE ARREGLA
   * ACÁ:** la frase dice «no se ven **ni se generan**», y en R4 **ya no se genera
   * nada** —no hay cola, ni archivo guardado, ni historial (`PRD_R4.md:1166`)—. Esas
   * tres palabras describen un mundo que esta misma etapa retira. **No se reescriben
   * desde acá**: el texto es de `R1.1-F4`, lo afirma TEXTUAL
   * `pruebas/r3-consola-de-un-brazo.test.tsx:293-298`, y cambiarlo es una decisión de
   * copy con tres voces (PRD_R1.1, Anexo B y PRD_R4) y dueño el TL.
   *
   * ⛔ **Se dibuja TAMBIÉN cuando la capacidad no se puede determinar**, y es a
   * propósito: el aislamiento por brazo es una propiedad de que esta pantalla
   * EXISTA, no de los datos que llegó a mostrar. Un operador parado en una consola
   * mal declarada necesita saber igual que lo que no ve tampoco incluiría el otro
   * brazo.
   *
   * ⟲ Se conserva la guarda de `L1` (30/9/2026): en una instancia de UN brazo la
   * frase hablaría de «el otro brazo», que no existe. Se dibuja sólo si la
   * declaración trae otro.
   */
  const notaDeAislamiento =
    brazo === null || losOtrosBrazos(brazo).length === 0 ? null : (
      <p className="exportacion__leyenda">
        La exportación es independiente por brazo: desde aquí no se ven ni se generan
        exportaciones del otro brazo.
      </p>
    )

  /**
   * ⛔⛔ **EL CORTE POR CAPACIDAD INDETERMINABLE** (docblock ①). No se dibuja la
   * tabla, no se dibuja el diccionario y **no se pide nada**: una tabla de doce
   * columnas sobre un brazo del que no sabemos si deriva es una afirmación que
   * nadie pudo verificar, y un CSV con esa forma sale del sistema.
   *
   * ⚠️ El encabezado y «Descargar CSV» tampoco se dibujan: el archivo tendría el
   * mismo problema que la tabla.
   */
  if (conDerivacion === null) {
    return (
      <section className="exportacion">
        <h1>Exportación</h1>
        {/*
          ⚠️ **El texto no nombra el campo de configuración ni el archivo**, y es
          deliberado: `R1.1-F5-CA-84` prohibe la jerga en pantalla y
          `pruebas/r11-textos.test.tsx` la mide (`configuracion.json` está en su
          lista). Lo que el operador necesita saber es QUÉ falta y A QUIÉN avisarle;
          el nombre del campo vive en el docblock ① de este archivo, que es donde lo
          busca quien lo va a arreglar. Sin voseo (`CA-85`).
        */}
        <p className="exportacion__error" role="alert">
          No se puede mostrar la exportación de este brazo: falta declarar si tiene derivación
          a humano, y de eso dependen las columnas «Pidió humano» de la tabla y del CSV.
          Avisa a quien administra esta instancia.
        </p>
        {notaDeAislamiento}
      </section>
    )
  }

  return (
    <section className="exportacion">
      {/*
        ⭐ `CA-05` · **el encabezado tiene SOLO «Descargar CSV»** —ni «Generar», ni
        «Exportaciones generadas», ni «No hay exportaciones»— y el botón es la
        acción principal de la pantalla (`PRD_R4.md:1159`).

        ⛔⛔ **«Descargando…» LO PONE LA PRIMITIVA Y NO ESTA PANTALLA.** El literal
        vive en `src/boton-de-descarga.tsx:63` (`{cargando ? "Descargando…" : rotulo}`)
        junto con el `disabled` y la guarda del doble disparo. Reimplementarlo acá
        sería la segunda copia que `ADR-0386` existe para impedir, y `CA-79` se
        rompería el día que `R4-UI0` corrija el indicador. Lo que esta pantalla
        aporta es ⓐ el RÓTULO —«Descargar CSV», que las otras dos pantallas no
        comparten— y ⓑ **dónde va el aviso de error**, que el PRD pone «Debajo del
        encabezado» (`:1269`) y por eso la primitiva lo ENTREGA por `onError` en
        vez de pintarlo adentro.
      */}
      <header className="exportacion__encabezado">
        <h1>Exportación</h1>
        <BotonDeDescarga
          rotulo="Descargar CSV"
          accion={descargar}
          onError={() => setErrorDeDescarga(DESCARGA_FALLIDA)}
        />
      </header>

      {/* ⭐ `CA-77` · el aviso de la descarga, debajo del encabezado y sin archivo. */}
      {errorDeDescarga !== null && (
        <p className="exportacion__error" role="alert">
          {errorDeDescarga}
        </p>
      )}

      {/*
        ⭐ `CA-06` · **los filtros son EXACTAMENTE tres** —Lotes, Desde y Hasta— con
        «Aplicar filtro» y «Limpiar».

        ⚠️ **La clase `exportacion__selector` se CONSERVA y no es un descuido.** Es
        la que está en la lista de PANELES de `estilos.css:1152`, y el docblock de
        esa lista declara que **no se puede des-enumerar** («ser un panel es una
        decisión semántica») y que su modo de falla conocido es que *«nada verifica
        que una pantalla nueva entre»* —ya pasó una vez: los lotes de Exportación
        vivían sueltos a sangre—. Esa lista está fuera de la sección de Exportación,
        o sea fuera del territorio de esta etapa; reusar la clase que ya está
        adentro es lo que mantiene el panel con su forma sin tocarla.
        `exportacion__filtros` trae encima la disposición del boceto.
      */}
      <div className="exportacion__selector exportacion__filtros">
        <fieldset className="exportacion__filtro">
          {/*
            ⭐ `CA-06` · el nombre del filtro. Va en `<legend>` y no en un `<p>`
            suelto: el desplegable es un BOTÓN cuyo nombre accesible es su rótulo
            («Todos los lotes»), que no dice de qué es. El `<legend>` nombra el
            grupo —el control y su ayuda— para quien navega con lector de pantalla.

            ⚠️ **Hueco de `R4-UI0`, declarado:** `SelectorDeLotes` no acepta
            `aria-label` ni `aria-labelledby`, así que el nombre del grupo es lo
            único que se puede dar desde acá. Dueño `ui-marco-primitivas`.
          */}
          <legend className="exportacion__rotulo">Lotes</legend>

          <SelectorDeLotes
            lotes={lotesParaElSelector}
            elegidos={lotesElegidos}
            rotulo={rotuloDelSelector(lotesElegidos)}
            onCambio={setLotesElegidos}
          />

          {/* ⭐ `CA-07`, textual (`PRD_R4.md:1181`). */}
          <p className="exportacion__ayuda">
            Sin ninguno elegido se muestran todos los lotes del brazo.
          </p>
        </fieldset>

        {/*
          ⚠️ **Las fechas NO van en un `<fieldset>` y es a propósito.** Un `<legend>`
          sería un rótulo de grupo que ni el boceto ni el PRD tienen —el boceto pone
          `rot` sobre cada campo y nada encima de los dos— y `CA-06` dice que los
          filtros son **exactamente** Lotes, Desde y Hasta: un cuarto rótulo
          («Fechas») es un filtro de más a la vista. Cada `<input>` tiene su propio
          `<label>`, que es lo que un control de fecha necesita para tener nombre.
        */}
        <div className="exportacion__filtro">
          <div className="exportacion__fechas">
            {/*
              ⛔ `type="date"` · el calendario del NAVEGADOR (`PRD_R4.md:1183`), y
              el valor que entrega es `aaaa-mm-dd`: así viaja al borde, sin pasar
              por `Date` (docblock ④).
            */}
            <label className="exportacion__fecha" htmlFor="exportacion-desde">
              Desde
              <input
                id="exportacion-desde"
                type="date"
                value={desdeEscrito}
                onChange={(evento) => setDesdeEscrito(evento.target.value)}
              />
            </label>

            <label className="exportacion__fecha" htmlFor="exportacion-hasta">
              Hasta
              <input
                id="exportacion-hasta"
                type="date"
                value={hastaEscrito}
                onChange={(evento) => setHastaEscrito(evento.target.value)}
              />
            </label>
          </div>

          {/* ⭐ `CA-07`, textual. */}
          <p className="exportacion__ayuda">
            Opcionales. Sin fechas se toma todo, desde el primer mensaje.
          </p>

          {/* ⭐ `CA-26` · debajo de las fechas y en color de error (`PRD_R4.md:1188`). */}
          {rangoInvertido && (
            <p className="exportacion__error" role="alert">
              Desde tiene que ser igual o anterior a Hasta.
            </p>
          )}
        </div>

        <div className="exportacion__acciones">
          {/*
            ⭐ `CA-26`/`CA-27` · deshabilitado mientras el rango está invertido, y
            **la tabla, el paginado y el CSV siguen con el último filtro aplicado**
            — que es lo que sale gratis de tener `aplicado` separado (docblock ②).
          */}
          <button type="button" onClick={aplicar} disabled={rangoInvertido}>
            Aplicar filtro
          </button>
          <button type="button" onClick={limpiar}>
            Limpiar
          </button>
        </div>
      </div>

      {/*
        ⭐ `CA-28`–`CA-34` · la leyenda de DOS líneas, las dos derivadas del filtro
        APLICADO y no del elegido: la leyenda describe lo que la tabla muestra.
      */}
      <p className="exportacion__leyenda">{primeraLineaDeLaLeyenda(brazo, aplicado)}</p>
      <p className="exportacion__ayuda">{segundaLineaDeLaLeyenda(aplicado)}</p>

      {/*
        ⛔ `PRD_R4.md:1207` · *«Si la tabla no entra en el ancho de la pantalla, se
        desplaza la tabla y no la página»*. Lo da `.tabla-rodante`, que es la
        primitiva de la casa para eso; `estilos.css` tiene medido por qué el
        `overflow-x` no puede ir en la sección (se iría el sello del brazo).
      */}
      <div className="tabla-rodante">
        <table className="exportacion__tabla">
          <thead>
            <tr>
              {columnas.map((columna) => (
                <th key={columna} scope="col">
                  {columna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/*
              ⛔ Regla 21 · **«Cargando…» va EN LUGAR DE las filas** (`PRD_R4.md:1265`),
              así que el `cargando` corta: no se dibuja ninguna fila vieja debajo.
            */}
            {cargando ? (
              <tr>
                <td colSpan={columnas.length} className="exportacion__celda-estado">
                  {CARGANDO}
                </td>
              </tr>
            ) : (
              <>
                {/*
                  ⛔ `CA-78` · el aviso va ADENTRO de la tabla —*«la tabla dice “No se
                  pudo hablar con el servidor.”»*— y **las filas que había quedan
                  debajo**, como el PRD pide. Un aviso afuera con la tabla vaciada
                  cumpliría la mitad del criterio y rompería la otra.
                */}
                {errorDeLista !== null && (
                  <tr>
                    <td
                      colSpan={columnas.length}
                      className="exportacion__celda-estado exportacion__celda-estado--error"
                      role="alert"
                    >
                      {errorDeLista}
                    </td>
                  </tr>
                )}

                {/* `PRD_R4.md:1260` · «no hay» es un RESULTADO, no un fallo de lectura. */}
                {errorDeLista === null && filas.length === 0 && (
                  <tr>
                    <td colSpan={columnas.length} className="exportacion__celda-estado">
                      {SIN_FILAS}
                    </td>
                  </tr>
                )}

                {filas.map((fila) => (
                  <tr key={fila.id}>
                    {/*
                      ⭐ `CA-37` · el ID acortado con su burbuja y «Copiar», con la
                      primitiva de R3 (`IdentificadorAcortado`). ⛔ El cable manda el
                      id **COMPLETO** (`contracts/analytics/index.ts:1424-1443`: el
                      acortado es de la PANTALLA) y la burbuja lo necesita entero.
                    */}
                    <td className="exportacion__id">
                      <IdentificadorAcortado identificador={fila.id} que="Participante" />
                    </td>
                    <td>{fila.nombre}</td>
                    {/* ⭐ `CA-38` · el teléfono completo, con «+», tal como llega. */}
                    <td>{fila.telefono}</td>
                    {/* El «—» del lote sin lote lo pone el servidor (columna 4). */}
                    <td>{fila.lote}</td>
                    <td>{fila.recibioMensajeInductorEnElRango}</td>
                    {/*
                      ⛔ `motivo.texto` tal cual: la frase la arma
                      `modules/analytics/.../filas.ts` §`FRASES_DEL_MOTIVO` y la de
                      «Fallido:» sale de `reasonFor`. Acá no hay tabla de motivos
                      (docblock ③).
                    */}
                    <td className="exportacion__motivo">{fila.motivo?.texto ?? ""}</td>
                    <td>{fila.sinInteraccion}</td>
                    <td>
                      <CeldaFechaHora fecha={fila.sinInteraccionFechaHora} />
                    </td>
                    <td>{fila.siguioConChatbot}</td>
                    <td>
                      <CeldaFechaHora fecha={fila.siguioConChatbotFechaHora} />
                    </td>
                    {/*
                      ⛔⛔ **LAS DOS CELDAS DE «Pidió humano», CONDICIONADAS POR LA
                      MISMA CAPACIDAD QUE LOS DOS `<th>` Y QUE EL DICCIONARIO.** La
                      de fecha va junto con la de estado y no por separado: el examen
                      ciego midió que condicionar una sola deja vivo el mutante que
                      pinta las catorce (docblock ①).
                    */}
                    {conDerivacion && (
                      <>
                        <td>{fila.pidioHumano ?? ""}</td>
                        <td>
                          <CeldaFechaHora fecha={fila.pidioHumanoFechaHora ?? ""} />
                        </td>
                      </>
                    )}
                    {/*
                      ⭐ `PRD_R4.md:1224` · Opt-out «Sí» va **con el estilo de alerta**.
                      La clase se pone sólo en el «Sí»: un «No» en rojo diría que algo
                      pasó cuando no pasó nada.
                    */}
                    <td>
                      {fila.optOut === "Sí" ? (
                        <span className="exportacion__alerta">{fila.optOut}</span>
                      ) : (
                        fila.optOut
                      )}
                    </td>
                    <td>
                      <CeldaFechaHora fecha={fila.optOutFechaHora} />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/*
        ⭐ `CA-68`–`CA-71` · el paginado de `R4-UI0` con la escala de R4.

        ⛔ **`conIrALaPagina={false}` es una decisión y va declarada.** El default de
        la primitiva es «la barra de R3 trae “Ir a la página”», y esta barra pide el
        selector de tamaño, así que sin el prop lo heredaría. `PRD_R4.md:1159`
        ENUMERA los elementos del paginado de esta pantalla —*«“Filas por página”
        con su ayuda, “Anterior”, “Página N de M · T participantes” y
        “Siguiente”»*— y «Ir a la página» no está en la lista, ni en §Orden y
        paginado (`:1258`), ni en ningún CA de F2. La primitiva declara este prop
        como «la salida declarada» para exactamente este caso.

        ⚠️ El `total` es el de la BASE y no `filas.length`: con 13 participantes y
        10 filas por página, `items.length` es 10 y el paginado diría «de 1».
      */}
      <Paginado
        etiqueta="la exportación"
        pagina={pagina}
        total={total}
        tamanoDePagina={filasPorPagina}
        unidad={{ singular: "participante", plural: "participantes" }}
        escala={ESCALA_DE_FILAS}
        onPagina={setPagina}
        onTamanoDePagina={cambiarFilasPorPagina}
        conIrALaPagina={false}
      >
        <span className="exportacion__ayuda-filas">{AYUDA_DE_FILAS}</span>
      </Paginado>

      {/*
        ⭐ `CA-72`/`CA-73` · el diccionario, DEBAJO del paginado (`PRD_R4.md:1271`).

        ⚠️ Cada entrada es un `<p>` con el nombre en `<strong>` y el texto después,
        como el boceto (`.pie-def > span > b`), y no un `<dl>`: con `<dt>`/`<dd>` el
        nombre y su texto son nodos HERMANOS y el `textContent` del bloque los pega
        sin espacio («Pidió humano:«Sí» si tocó…»), que es como se lee la entrada
        completa. El párrafo mantiene la frase entera legible en una sola lectura.
      */}
      <section className="exportacion__diccionario">
        <h2 className="exportacion__rotulo">Qué significa cada columna</h2>
        {DICCIONARIO.filter((fila) => fila.soloConDerivacion !== true || conDerivacion).map(
          (fila) => (
            <p key={fila.entrada}>
              <strong>{fila.entrada}</strong> {fila.texto}
            </p>
          ),
        )}
      </section>

      {/*
        ⭐ `CA-74`/`CA-75` · la nota del brazo, debajo del diccionario y **sólo donde
        la derivación está declarada**. `.nota-brazo` ya es la clase de la casa para
        esto (`estilos.css:7702`), así que no nace una segunda forma de nota.
      */}
      {conDerivacion && <p className="nota-brazo">{NOTA_DEL_BRAZO}</p>}

      {notaDeAislamiento}
    </section>
  )
}
